import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@rtkwlf/pro-solid-svg-icons/faHome';

import { ClickAwayListener } from '../../../components';
import { Divider } from '../../../components/divider';
import { Drawer } from '../../../components/drawer';
import { Stack } from '../../../components/stack';
import { Typography } from '../../../components/typography';
import { useFenrirTranslation } from '../../../locales/use-fenrir-translation';
import type { Theme, CSSObject } from '../../../styles';
import { styled } from '../../../styles';

import { CollapseButton } from './collapse-button';
import { NavigationMenu, type MenuItem } from './navigation-menu';
import { useNavigation } from './navigation-provider';

const SIDEBAR_WIDTH = 375;

export interface NavigationProps {
  applicationName: string;
  primaryMenu?: MenuItem[];
  secondaryMenu?: MenuItem[];
  isLoading?: boolean;
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) =>
    prop !== 'isNavOverlayExpanded' && prop !== 'isNavSnapExpanded',
})<{
  isNavOverlayExpanded: boolean;
  isNavSnapExpanded: boolean;
}>(({ theme }) => ({
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',

  variants: [
    {
      props: ({ isNavSnapExpanded }) => isNavSnapExpanded,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ isNavOverlayExpanded }) => isNavOverlayExpanded,
      style: {
        width: `calc(${theme.spacing(7)} + 1px)`,

        '& .MuiDrawer-paper': {
          ...openedMixin(theme),
          position: 'absolute',
        },
      },
    },
    {
      props: ({ isNavOverlayExpanded, isNavSnapExpanded }) =>
        !isNavOverlayExpanded && !isNavSnapExpanded,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

/** @experimental In active development. Avoid using until further notice. */
export const Navigation = ({
  applicationName,
  primaryMenu,
  secondaryMenu,
  isLoading,
}: NavigationProps) => {
  const { t } = useFenrirTranslation({ keyPrefix: 'dashboardLayout' });
  const {
    isNavSnapExpanded,
    isNavOverlayExpanded,
    toggleNavigation,
    closeOverlayNavigation,
  } = useNavigation();

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={closeOverlayNavigation}
    >
      <StyledDrawer
        variant="permanent"
        isNavOverlayExpanded={isNavOverlayExpanded}
        isNavSnapExpanded={isNavSnapExpanded}
        sx={{ displayPrint: 'none' }}
      >
        <Stack
          direction="row"
          sx={(theme) => ({
            alignItems: 'center',
            // align icon with navigation item icon
            paddingLeft: '10px',
            // align title with navigation item text
            gap: theme.spacing(3.5),
            minHeight: theme.spacing(8),
          })}
        >
          <CollapseButton open={isNavSnapExpanded} onClick={toggleNavigation} />
          <Typography data-testid="dashboard-application-name">
            {applicationName}
          </Typography>
        </Stack>

        <Divider />
        <NavigationMenu
          isLoading={isLoading}
          primaryMenu={[
            {
              title: t('navigation.home'),
              icon: <FontAwesomeIcon icon={faHome} />,
              segment: '',
              matchSegmentOnly: true,
            },
            ...(primaryMenu || []),
          ]}
          secondaryMenu={secondaryMenu}
        />
      </StyledDrawer>
    </ClickAwayListener>
  );
};
